import React, { useEffect, useState, useCallback } from "react";
import { usersAPI } from "../../utils/usersAPI";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import UserProfileModal from "../../components/UserProfileModal";
import Pagination from "../../components/Pagination";
import { useTranslation } from "react-i18next";

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserForModal, setSelectedUserForModal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [isSendingResetLink, setIsSendingResetLink] = useState(false); // Add this state variable
  const { t } = useTranslation();

  const loadUsers = useCallback(async (page = 1, searchTerm = "") => {
    try {
      const params = { page, page_size: 15 };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await usersAPI.list(params);
      const data = response?.data || response;
      setUsers(Array.isArray(data) ? data : data?.results || []);
      if (response?.pagination) setPagination(response.pagination);
    } catch (err) {
      showToast.error(
        err.message || t("admin.loadUsersFailed", "Failed to load users"),
      );
    }
  }, []);

  useEffect(() => {
    loadUsers(currentPage, search);
  }, [currentPage, search, loadUsers]);

  useEffect(() => {
    loadOrganizations();
  }, []);

  // Real-time search: debounce 300ms so results update as user types
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.list({ page_size: 100 });
      const data = response?.data || response;
      setOrganizations(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      showToast.error(
        err.message ||
          t("admin.loadOrganizationsFailed", "Failed to load organizations"),
      );
    }
  };

  const handleSelectUser = async (userId) => {
    try {
      const data = await usersAPI.get(userId);
      setSelectedUserForModal(data);
      setIsModalOpen(true);
    } catch (err) {
      showToast.error(
        err.message ||
          t("admin.loadUserDetailsFailed", "Failed to load user details"),
      );
    }
  };

  const handleEditUser = async (userId) => {
    try {
      const data = await usersAPI.get(userId);
      setSelectedUser(data);
      setIsModalOpen(false);
    } catch (err) {
      showToast.error(
        err.message ||
          t("admin.loadUserDetailsFailed", "Failed to load user details"),
      );
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setSaving(true);
      const payload = {
        organization:
          selectedUser.role === "ANALYST"
            ? null
            : selectedUser.organization || null,
      };
      if (selectedUser.role === "ANALYST") {
        payload.identification_id = selectedUser.identification_id ?? "";
        payload.phone = selectedUser.phone ?? "";
      }
      const updated = await usersAPI.update(selectedUser.id, payload);
      setSelectedUser(updated);
      showToast.success(t("admin.userUpdated", "User updated successfully"));
      loadUsers(currentPage);
    } catch (err) {
      showToast.error(
        err.message || t("admin.updateUserFailed", "Failed to update user"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteUser = async (user) => {
    const confirmed = await showConfirm({
      title: t("actions.deleteUser", "Delete user?"),
      message: t(
        "actions.deleteUserConfirm",
        `Are you sure you want to delete ${user.email}? This action cannot be undone.`,
      ),
      confirmText: t("actions.deleteUser", "Delete"),
      cancelText: t("actions.cancel", "Cancel"),
      type: "danger",
    });
    if (!confirmed) return;
    try {
      await usersAPI.remove(user.id);
      showToast.success(t("admin.userDeleted", "User deleted successfully"));
      loadUsers(currentPage, search);
    } catch (err) {
      showToast.error(
        err.message || t("admin.deleteUserFailed", "Failed to delete user"),
      );
    }
  };

  const handleSendResetLink = async (user) => {
    setIsSendingResetLink(true); // Set loading state to true
    try {
      await usersAPI.sendClientResetLink(user.id);
      showToast.success(
        t("admin.resetLinkSent", "Password reset link sent to client"),
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err.message ||
        t("admin.sendResetFailed", "Failed to send reset link");
      showToast.error(msg);
    } finally {
      setIsSendingResetLink(false); // Reset loading state
    }
  };

  const handleOpenAnalystPasswordModal = (user) => {
    setPasswordModalUser(user);
    setPasswordValue("");
  };

  const handleSaveAnalystPassword = async (e) => {
    e.preventDefault();
    if (!passwordModalUser || !passwordValue.trim()) {
      showToast.error(
        t("admin.enterNewPassword", "Please enter a new password."),
      );
      return;
    }
    try {
      setPasswordSaving(true);
      await usersAPI.setAnalystPassword(passwordModalUser.id, {
        password: passwordValue.trim(),
      });
      showToast.success(
        t("admin.analystPasswordUpdated", "Analyst password updated"),
      );
      setPasswordModalUser(null);
      setPasswordValue("");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err.message ||
        t(
          "admin.updateAnalystPasswordFailed",
          "Failed to update analyst password",
        );
      showToast.error(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="page-content">
      <div className="card">
        <h2 className="card-title">{t("admin.users", "Users")}</h2>
        <div
          className="form-group"
          style={{ maxWidth: "320px", marginBottom: "16px" }}
        >
          <label>{t("common.search", "Search")}</label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t(
              "admin.searchUsersPlaceholder",
              "Search by name or email...",
            )}
          />
        </div>
        {users.length > 0 ? (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t("common.name", "Name")}</th>
                  <th>{t("auth.email", "Email")}</th>
                  <th>{t("common.role", "Role")}</th>
                  <th>{t("platform.organization", "Organization")}</th>
                  <th>{t("common.status", "Status")}</th>
                  <th>{t("common.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      {user.first_name} {user.last_name}
                    </td>
                    <td>{user.email}</td>
                    <td>{user.role}</td>
                    <td>{user.organization_name || "-"}</td>
                    <td>
                      {user.is_active
                        ? t("common.active", "Active")
                        : t("common.pending", "Pending")}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "6px",
                          justifyContent: "flex-start",
                        }}
                      >
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleSelectUser(user.id)}
                        >
                          {t("actions.view", "View")}
                        </button>
                        {user.role === "CLIENT" && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleSendResetLink(user)}
                            disabled={isSendingResetLink} // Disable button while loading
                          >
                            {isSendingResetLink
                              ? "Sending..."
                              : "Send Reset Link"}{" "}
                            {/* Show loader */}
                          </button>
                        )}
                        {user.role === "ANALYST" && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleOpenAnalystPasswordModal(user)}
                          >
                            {t("actions.resetPassword")}
                          </button>
                        )}
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleDeleteUser(user)}
                        >
                          {t("actions.deleteUser", "Delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <p style={{ padding: "16px", textAlign: "center" }}>
            {t("admin.noUsers", "No users found.")}
          </p>
        )}
      </div>

      <UserProfileModal
        user={selectedUserForModal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {selectedUser && (
        <div className="card" style={{ marginTop: "24px" }}>
          <h3 className="card-title">{t("admin.editUser", "Edit User")}</h3>
          <p style={{ color: "var(--text-grey)", marginTop: "6px" }}>
            {selectedUser.email} • {selectedUser.role}
          </p>

          <form
            onSubmit={handleUpdateUser}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              marginTop: "16px",
            }}
          >
            {selectedUser.role !== "ANALYST" && (
              <div className="form-group">
                <label>{t("platform.organization", "Organization")}</label>
                <select
                  value={selectedUser.organization || ""}
                  onChange={(e) =>
                    setSelectedUser((prev) => ({
                      ...prev,
                      organization: e.target.value,
                    }))
                  }
                >
                  <option value="">None</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedUser.role === "ANALYST" && (
              <>
                <div className="form-group">
                  <label>
                    {t("profile.nationalId", "Identification ID (Rwandan)")}
                  </label>
                  <input
                    type="text"
                    value={selectedUser.identification_id || ""}
                    onChange={(e) =>
                      setSelectedUser((prev) => ({
                        ...prev,
                        identification_id: e.target.value,
                      }))
                    }
                    placeholder="e.g. 1xxxxxxxxxxxx"
                  />
                </div>
                <div className="form-group">
                  <label>{t("profile.phoneRwandan", "Phone (Rwandan)")}</label>
                  <input
                    type="text"
                    value={selectedUser.phone || ""}
                    onChange={(e) =>
                      setSelectedUser((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    placeholder="e.g. +250 788 123 456"
                  />
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving
                  ? t("common.saving", "Saving...")
                  : t("actions.saveChanges")}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelectedUser(null)}
              >
                {t("actions.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}

      {passwordModalUser && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setPasswordModalUser(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: "420px",
              width: "100%",
              padding: "20px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="card-title">Reset Analyst Password</h3>
            <p style={{ color: "var(--text-grey)", marginTop: "4px" }}>
              {passwordModalUser.email}
            </p>
            <form
              onSubmit={handleSaveAnalystPassword}
              style={{
                marginTop: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div className="form-group">
                <label>{t("admin.newPassword", "New Password")}</label>
                <input
                  type="password"
                  value={passwordValue}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  placeholder="Enter new password"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPasswordModalUser(null)}
                  disabled={passwordSaving}
                >
                  {t("actions.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={passwordSaving}
                >
                  {passwordSaving
                    ? t("common.saving", "Saving...")
                    : t("admin.savePassword", "Save Password")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUsers;
